import CreationProcessView from '@/views/admin/organization/creation-process/creation-process-view.vue';
import AdminOrgView from '@/views/admin/organization/organization-view.vue';
import UserCreationView from '@/views/admin/user/user-creation-view.vue';
import UserView from '@/views/admin/user/established-user-view.vue';
import { createRouter, createWebHistory } from 'vue-router';
import PlaygroundView from '@/views/playground-view.vue';
import {
    ADMIN_CONTACT_ROLES,
    ADMIN_EVENT_HUB_ROLES,
    ADMIN_INCIDENT_ROLES,
    ADMIN_LOCATION_ROLES,
    ADMIN_ORGANIZATION_ROLES,
    ADMIN_ROLE_ROLES,
    ADMIN_ACCESS_CARD_READ,
} from './admin-access';
import { orgWizardRouteGuard } from '@/views/admin/organization/org-wizard/org-wizard-routing';

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        {
            path: '/',
            meta: { requiresAuth: true },
            children: [
                {
                    path: '',
                    name: 'organizations',
                    meta: { allowedSystemRoles: [...ADMIN_ORGANIZATION_ROLES] },
                    component: AdminOrgView,
                },
                {
                    path: '/organization/:id',
                    name: 'org-detail',
                    meta: { allowedSystemRoles: [...ADMIN_ORGANIZATION_ROLES] },
                    props: true,
                    component: () => import('@/views/admin/organization/organization-detail-view.vue'),
                },
                {
                    path: '/organization/create',
                    name: 'org-create',
                    meta: { requiresAuth: true, allowedSystemRoles: ['cdim-admin'] },
                    component: CreationProcessView,
                },
                {
                    path: '/organization/create/:form_id',
                    name: 'org-edit',
                    meta: { requiresAuth: true, allowedSystemRoles: ['cdim-admin', 'noc-admin'] },
                    props: (route) => ({ formId: route.params.form_id }),
                    component: CreationProcessView,
                },
                // New 4-step wizard (ITDEVELOP-3227), behind the slaOrgWizard feature flag.
                // Replaces org-create/org-edit once the flag graduates. "Wizard" stays an
                // internal name (route names, files); the URL and UI say "new organization".
                {
                    path: '/organization/new',
                    name: 'org-wizard-create',
                    meta: { requiresAuth: true, allowedSystemRoles: ['cdim-admin'] },
                    beforeEnter: orgWizardRouteGuard,
                    component: () => import('@/views/admin/organization/org-wizard/org-wizard-view.vue'),
                },
                {
                    path: '/organization/new/:draftId',
                    name: 'org-wizard-edit',
                    meta: { requiresAuth: true, allowedSystemRoles: ['cdim-admin', 'noc-admin'] },
                    beforeEnter: orgWizardRouteGuard,
                    props: true,
                    component: () => import('@/views/admin/organization/org-wizard/org-wizard-view.vue'),
                },
                {
                    path: '/dev/playground',
                    name: 'playground',
                    component: PlaygroundView,
                    meta: { requiresAuth: true },
                },
            ],
        },
        {
            path: '/users',
            meta: { requiresAuth: true, allowedSystemRoles: [...ADMIN_ORGANIZATION_ROLES] },
            children: [
                {
                    path: '',
                    name: 'users',
                    component: UserView,
                },
                {
                    path: 'create',
                    name: 'user-creation-process',
                    component: UserCreationView,
                },
                {
                    path: 'impersonation-request',
                    name: 'request-impersonation',
                    component: () => import('@/views/admin/user/request-impersonation.vue'),
                },
                {
                    path: ':userId/impersonations/:impersonationRequestId',
                    name: 'user-impersonation-request-details',
                    component: () => import('@/views/admin/user/user-impersonation-request-details.vue'),
                    props: true,
                },
                {
                    path: ':userId',
                    name: 'user-details',
                    component: () => import('@/views/admin/user/user-details-view.vue'),
                    props: true,
                },
            ],
        },
        {
            path: '/established-contacts',
            meta: { requiresAuth: true, allowedSystemRoles: [...ADMIN_CONTACT_ROLES] },
            children: [
                {
                    path: '',
                    name: 'contacts',
                    component: () => import('@/views/admin/contact/established-contact-view.vue'),
                },
                {
                    path: 'assign',
                    name: 'assign-contacts',
                    component: () => import('@/views/admin/contact/assign-contact-view.vue'),
                },
            ],
        },
        {
            path: '/contact-types',
            name: 'contact-types',
            meta: { requiresAuth: true, allowedSystemRoles: [...ADMIN_CONTACT_ROLES] },
            component: () => import('@/views/admin/contact/contact-type-view.vue'),
        },
        {
            path: '/notifications',
            meta: { requiresAuth: true, allowedSystemRoles: ['employee'], hasNotificationAccess: true },
            children: [
                {
                    path: '',
                    name: 'notifications',
                    component: () => import('@/views/notifications/notifications-view.vue'),
                },
                {
                    path: ':id/:orgId',
                    name: 'notification-details',
                    props: true,
                    component: () => import('@/views/notifications/notification-detail-view.vue'),
                },
                {
                    path: 'subscriptions',
                    name: 'subscriptions',
                    component: () => import('@/views/notifications/edit-subscription-view.vue'),
                },
            ],
        },
        {
            path: '/roles',
            meta: { requiresAuth: true, allowedSystemRoles: [...ADMIN_ROLE_ROLES] },
            children: [
                {
                    path: '',
                    name: 'organization-roles',
                    component: () => import('@/views/admin/roles/organization-roles.vue'),
                },
                {
                    path: '/role/:roleId',
                    name: 'organization-role',
                    props: true,
                    component: () => import('@/views/admin/roles/organization-role.vue'),
                },
                // {
                //     path: 'system-roles',
                //     name: 'system-roles',
                //     component: () => import('@/views/admin/roles/system-roles.vue'),
                // },
            ],
        },
        {
            path: '/event-hub',
            meta: {
                requiresAuth: true,
                // allowedSystemRoles: ['cdim-admin', 'noc-admin']
            },
            children: [
                {
                    path: '',
                    name: 'event-hub',
                    meta: { allowedSystemRoles: [...ADMIN_EVENT_HUB_ROLES] },
                    component: () => import('@/views/notifications/event-hub-overview.vue'),
                },
                {
                    path: 'create',
                    name: 'notification-create',
                    meta: { allowedSystemRoles: ['noc-admin'] },
                    component: () => import('@/views/notifications/create-notification-view.vue'),
                },
                {
                    path: 'edit/:notificationParentId',
                    name: 'notification-edit',
                    props: true,
                    meta: { allowedSystemRoles: ['noc-admin'] },
                    component: () => import('@/views/notifications/create-notification-view.vue'),
                },
                {
                    path: ':id',
                    name: 'event-hub-notification-admin-details',
                    props: true,
                    meta: { allowedSystemRoles: [...ADMIN_EVENT_HUB_ROLES] },
                    component: () => import('@/views/notifications/notification-detail-view.vue'),
                },
            ],
        },
        {
            path: '/visitors',
            meta: { requiresAuth: true, allowedSystemRoles: [...ADMIN_ORGANIZATION_ROLES] },
            children: [
                {
                    path: '',
                    name: 'visitors',
                    component: () => import('@/views/visitors/visitors-overview.vue'),
                },
                {
                    path: ':id',
                    name: 'visitor-details',
                    props: true,
                    component: () => import('@/views/visitors/visitor-details-view.vue'),
                },
                {
                    path: ':id/edit',
                    name: 'visitor-edit',
                    props: true,
                    component: () => import('@/views/visitors/edit-visitor-view.vue'),
                },
                {
                    path: 'create',
                    name: 'create-visitor',
                    component: () => import('@/views/visitors/create-visitor-view.vue'),
                },
            ],
        },
        {
            path: '/infrastructure',
            meta: {
                requiresAuth: true,
                allowedSystemRoles: [...ADMIN_LOCATION_ROLES],
            },
            children: [
                {
                    path: '',
                    name: 'locations',
                    component: () => import('@/views/admin/infrastructure/locations-view.vue'),
                },
            ],
        },
        {
            path: '/incidents',
            meta: {
                requiresAuth: true,
                allowedSystemRoles: [...ADMIN_INCIDENT_ROLES],
            },
            children: [
                {
                    path: '',
                    name: 'noc-incidents',
                    component: () => import('@/views/admin/incidents/noc-incidents-view.vue'),
                },
                // NOC create-incident flow — reuses the customer/org incident form views
                // (01/02/03). The shared views resolve their navigation targets from
                // meta.incidentFlowScope, so the same components drive the admin/NOC flow.
                // The static `create*` paths must precede `:incidentKey` so they are not
                // swallowed by the dynamic detail route.
                {
                    path: 'create',
                    name: 'noc-select-incident-category',
                    meta: { incidentFlowScope: 'noc', hideNavBar: true },
                    props: true,
                    component: () => import('@/views/incidents/01-select-incident-type.vue'),
                },
                {
                    path: 'category/:incidentCategoryId/type/:incidentTypeId/create',
                    name: 'noc-create-incident-form-parent',
                    meta: { incidentFlowScope: 'noc', hideNavBar: true },
                    props: true,
                    component: () => import('@/views/incidents/02-create-incident-form-parent.vue'),
                },
                {
                    path: 'create/success',
                    name: 'noc-creation-success',
                    meta: { incidentFlowScope: 'noc', hideNavBar: true },
                    props: true,
                    component: () => import('@/views/incidents/03-creation-success.vue'),
                },
                {
                    path: 'create/error',
                    name: 'noc-creation-error',
                    meta: { incidentFlowScope: 'noc', hideNavBar: true },
                    props: true,
                    component: () => import('@/views/incidents/04-creation-error.vue'),
                },
                {
                    path: ':incidentKey',
                    name: 'noc-incident-details',
                    props: true,
                    component: () => import('@/views/admin/incidents/noc-incident-detail-view.vue'),
                },
            ],
        },
        {
            path: '/access-cards',
            name: 'access-cards',
            meta: {
                requiresAuth: true,
                requiredOrganizationSchemeRoles: [ADMIN_ACCESS_CARD_READ],
            },
            component: () => import('@/views/admin/access-cards/access-cards-view.vue'),
        },
        {
            path: '/login',
            name: 'login',
            meta: { hideNavBar: true },
            component: () => import('@/views/login-view.vue'),
        },
        {
            path: '/reset/password',
            name: 'reset',
            meta: { hideNavBar: true },
            component: () => import('@/views/update-pass.vue'),
        },
        {
            path: '/loading',
            name: 'loading',
            props: true,
            component: () => import('@/views/loading-view.vue'),
        },
        {
            path: '/:catchAll(.*)',
            name: 'not-found',
            component: () => import('@/views/not-found-view.vue'),
        },
    ],
});

export default router;
